import { PageLayout } from '@repo/ui/components/page-layout'
import { createFileRoute } from '@tanstack/react-router'
import { CloudOff, Star } from 'lucide-react'
import { useTranslation } from '@/lib/i18n'
import { NodeDetails } from '@/modules/file-manager/components/details-node'
import { DetailsPanel } from '@/modules/file-manager/components/details-panel'
import { FilePreview } from '@/modules/file-manager/components/file-preview'
import { FolderBar } from '@/modules/file-manager/components/folder-bar'
import {
	ListingError,
	ListingState
} from '@/modules/file-manager/components/listing-states'
import { FOLDER_COLUMNS } from '@/modules/file-manager/components/node-columns'
import { NodeGrid } from '@/modules/file-manager/components/node-grid'
import { useFoldersFirst } from '@/modules/file-manager/hooks/use-folders-first'
import { ListingProvider } from '@/modules/file-manager/hooks/use-listing'
import {
	favoritesSearchMiddlewares,
	validateFavoritesSearch
} from '@/modules/file-manager/lib/folder-search'
import { useFavorites } from '@/modules/file-manager/query/use-favorites'
import { GlobalSearch } from '@/modules/search/components/global-search'

/**
 * «Speciali»: the nodes the caller starred, flat, each row with the role the
 * caller really holds on it.
 *
 * Built like «Condivisi con me»: roots with no folder around them, so
 * `parentId` and `parentRole` are `null` and nothing can be moved, dropped or
 * created here. The order is the folders' (`name`, `asc` by default), without
 * `lastViewed`. Removing a
 * favorite re-reads the list, so the row leaves with the mutation.
 */
export const Route = createFileRoute('/(private)/(shell)/favorites/')({
	component: FavoritesPage,
	search: { middlewares: favoritesSearchMiddlewares },
	validateSearch: validateFavoritesSearch
})

function FavoritesPage() {
	const { t } = useTranslation()
	const search = Route.useSearch()
	const foldersFirst = useFoldersFirst()
	const { query, roots } = useFavorites(
		{
			by: search.by,
			direction: search.direction,
			foldersFirst
		},
		{ categories: search.categories, people: search.people }
	)

	return (
		<ListingProvider nodes={roots} parentId={null} parentRole={null}>
			<PageLayout
				aside={
					<DetailsPanel>
						<NodeDetails />
					</DetailsPanel>
				}
				bar={<FolderBar favorites />}
				breadcrumb={
					<h1 className='min-w-0 flex-1 font-medium text-base'>
						{t('client:favorites.title')}
					</h1>
				}
				search={<GlobalSearch />}
			>
				<NodeGrid
					columns={FOLDER_COLUMNS}
					empty={
						<ListingState
							description={t(
								'client:favorites.empty.description'
							)}
							icon={<Star />}
							title={t('client:favorites.empty.title')}
						/>
					}
					error={
						<ListingError
							description={t(
								'client:favorites.error.description'
							)}
							onRetry={() => query.refetch()}
							title={t('client:favorites.error.title')}
						/>
					}
					filteredDescription='client:files.filters.empty.description_favorites'
					nodes={roots}
					offline={
						<ListingState
							description={t(
								'client:favorites.offline.description'
							)}
							icon={<CloudOff />}
							title={t('client:files.offline.title')}
						/>
					}
					query={query}
				/>
			</PageLayout>

			<FilePreview />
		</ListingProvider>
	)
}
